import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import type { Page, Pageable, RangeLong } from '../../core/api/spring-types';

interface UseSpringPageQueryOptions<TData, TFilter> {
    queryKey: string[];
    fetchFn: (filter: TFilter, pageable: Pageable) => Promise<Page<TData>>;
    initialFilter?: TFilter;
    initialPageable?: Pageable;
    queryOptions?: Partial<UseQueryOptions<Page<TData>>>;
}

export function useSpringPageQuery<TData, TFilter = any>({
    queryKey,
    fetchFn,
    initialFilter,
    initialPageable,
    queryOptions
}: UseSpringPageQueryOptions<TData, TFilter>) {
    // State for filters
    const [filter, setFilter] = useState<TFilter>(
        initialFilter || {} as TFilter
    );

    // State for pagination
    const [pageable, setPageable] = useState<Pageable>(
        initialPageable || { page: 0, size: 20, sort: [] }
    );

    // Query with optional overrides
    const query = useQuery({
        queryKey: [...queryKey, filter, pageable],
        queryFn: () => fetchFn(filter, pageable),
        // No placeholder data - show loading state on every fetch
        ...queryOptions, // Allow consumer to override defaults
    });

    // Pagination controls
    const goToPage = useCallback((page: number) => {
        setPageable(prev => ({ ...prev, page }));
    }, []);

    const changePageSize = useCallback((size: number) => {
        setPageable(prev => ({ ...prev, size, page: 0 }));
    }, []);

    const changeSort = useCallback((field: string, direction: 'asc' | 'desc') => {
        setPageable(prev => ({
            ...prev,
            sort: [`${field},${direction}`],
            page: 0
        }));
    }, []);

    // Filter controls
    const updateFilter = useCallback((newFilter: Partial<TFilter>) => {
        setFilter(prev => ({ ...prev, ...newFilter }));
        setPageable(prev => ({ ...prev, page: 0 }));
    }, []);

    const updateRangeFilter = useCallback((
        field: string,
        range: RangeLong
    ) => {
        setFilter(prev => ({
            ...prev,
            [`${field}Range`]: range
        } as TFilter));
        setPageable(prev => ({ ...prev, page: 0 }));
    }, []);

    const resetFilters = useCallback(() => {
        setFilter(initialFilter || {} as TFilter);
        setPageable(prev => ({ ...prev, page: 0 }));
    }, [initialFilter]);

    return {
        // Query state
        data: query.data?.content || [],
        page: query.data,
        isLoading: query.isLoading,
        isError: query.isError,
        error: query.error,
        isFetching: query.isFetching,

        // Combined loading state (for DataTable and other components)
        // Shows loading during both initial load AND refetch (filtering/sorting)
        loading: query.isLoading || query.isFetching,

        // Pagination state & controls
        currentPage: pageable.page || 0,
        pageSize: pageable.size || 20,
        totalPages: query.data?.totalPages || 0,
        totalElements: query.data?.totalElements || 0,
        goToPage,
        changePageSize,
        changeSort,

        // Sorting state
        currentSort: pageable.sort?.[0] || null,
        sortField: pageable.sort?.[0]?.split(',')[0] || null,
        sortDirection: (pageable.sort?.[0]?.split(',')[1] as 'asc' | 'desc') || null,

        // Filter state & controls
        filter,
        updateFilter,
        updateRangeFilter,
        resetFilters,

        // Refresh
        refetch: query.refetch,
    };
}